/**
 * Phase 293 — Razorpay Health Check Wizard
 *
 * Renders a 3-step verification flow in the Academy Owner's Payments tab:
 *   1. Gateway auth handshake (live Razorpay /v1/orders ping)
 *   2. Webhook URL reachability (public endpoint exists)
 *   3. Verified badge issued + in-app celebration notification
 *
 * Wires to:
 *   POST /api/academy/razorpay/health-check
 *   GET  /api/academy/razorpay/health-status
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2, XCircle, Loader2, Stethoscope, ShieldCheck, AlertTriangle, Globe, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const STEP_LABELS = {
  gateway: 'Razorpay gateway handshake',
  webhook: 'Webhook URL reachable from internet',
  badge:   'Issue Payment-Verified badge',
};

function StepRow({ status, label, detail, testid }) {
  const Icon = status === 'success' ? CheckCircle2
    : status === 'failure' ? XCircle
    : status === 'running' ? Loader2
    : null;
  const tone = status === 'success' ? 'text-emerald-600'
    : status === 'failure' ? 'text-rose-600'
    : status === 'running' ? 'text-indigo-600'
    : 'text-slate-400';
  return (
    <div
      className="flex items-start gap-3 p-2.5 rounded-md border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900"
      data-testid={testid}
    >
      <div className={`flex-shrink-0 ${tone}`}>
        {Icon ? <Icon className={`h-4 w-4 ${status === 'running' ? 'animate-spin' : ''}`} /> : <div className="h-4 w-4 rounded-full border border-slate-300 dark:border-slate-700" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">{label}</p>
        {detail && <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">{detail}</p>}
      </div>
    </div>
  );
}

export default function RazorpayHealthCheckWizard({ razorpayConfigured = false, onComplete }) {
  const [running, setRunning] = useState(false);
  const [stepStatus, setStepStatus] = useState({ gateway: 'idle', webhook: 'idle', badge: 'idle' });
  const [stepDetail, setStepDetail] = useState({});
  const [persisted, setPersisted] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get('/academy/razorpay/health-status');
      setPersisted(r.data?.razorpay_health || null);
    } catch (err) {
      // silent — wizard works in standalone mode too
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runHealthCheck = useCallback(async () => {
    setRunning(true);
    setStepStatus({ gateway: 'running', webhook: 'idle', badge: 'idle' });
    setStepDetail({});
    try {
      const publicBaseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      // Start with gateway visually-running, swap based on full response
      const r = await api.post('/academy/razorpay/health-check', { public_base_url: publicBaseUrl });
      const data = r.data || {};
      const checks = data.checks || {};
      const gw = checks.gateway || {};
      const wh = checks.webhook || {};

      const gwOk = !!gw.ok;
      const whOk = !!wh.ok;
      const badgeOk = !!data.ok;

      setStepStatus({
        gateway: gwOk ? 'success' : 'failure',
        webhook: whOk ? 'success' : 'failure',
        badge: badgeOk ? 'success' : 'failure',
      });
      setStepDetail({
        gateway: gwOk
          ? `Authenticated · ${gw.key_environment || '?'} mode · ${gw.latency_ms ?? '?'} ms`
          : (gw.reason || gw.error || 'Razorpay rejected the credentials'),
        webhook: whOk
          ? `Reachable · HTTP ${wh.status} · ${wh.latency_ms ?? '?'} ms`
          : (wh.error || `Unreachable · HTTP ${wh.status ?? '?'}`),
        badge: badgeOk
          ? (data.first_verification
            ? '✓ Payment infrastructure verified — celebration notification sent.'
            : '✓ Payment infrastructure verified.')
          : 'Skipped — fix the failing step above and re-run.',
      });

      if (badgeOk) toast.success('Payment infrastructure verified ✓');
      else toast.error('Health check incomplete — see step details below');

      await loadStatus();
      if (typeof onComplete === 'function') onComplete(data);
    } catch (err) {
      setStepStatus({ gateway: 'failure', webhook: 'failure', badge: 'failure' });
      setStepDetail({ gateway: err?.response?.data?.detail || 'Network error' });
      toast.error('Health check failed to run');
    } finally {
      setRunning(false);
    }
  }, [loadStatus, onComplete]);

  const verified = !!persisted?.verified_at;
  const lastChecked = persisted?.last_checked_at;

  return (
    <div
      className="bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/30 dark:to-slate-900 rounded-lg p-4 border border-emerald-200 dark:border-emerald-900"
      data-testid="razorpay-health-wizard"
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-emerald-100 dark:bg-emerald-950">
            <Stethoscope className="h-4 w-4 text-emerald-600" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Payment Infrastructure Health Check
            </h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Run all 3 checks in ~3 seconds. No real money moves.
            </p>
          </div>
        </div>
        {verified && (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wide"
            data-testid="razorpay-health-verified-badge"
          >
            <ShieldCheck className="h-3 w-3" /> Verified
          </span>
        )}
      </div>

      <div className="space-y-2 mb-3">
        <StepRow
          status={stepStatus.gateway}
          label={STEP_LABELS.gateway}
          detail={stepDetail.gateway}
          testid="razorpay-health-step-gateway"
        />
        <StepRow
          status={stepStatus.webhook}
          label={STEP_LABELS.webhook}
          detail={stepDetail.webhook}
          testid="razorpay-health-step-webhook"
        />
        <StepRow
          status={stepStatus.badge}
          label={STEP_LABELS.badge}
          detail={stepDetail.badge}
          testid="razorpay-health-step-badge"
        />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button
          onClick={runHealthCheck}
          disabled={running || !razorpayConfigured}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="razorpay-health-run-btn"
          title={!razorpayConfigured ? 'Save your Razorpay Key ID + Secret first' : 'Run all 3 checks'}
        >
          {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {running ? 'Running checks…' : verified ? 'Re-run Health Check' : 'Run Full Health Check'}
        </Button>
        {lastChecked && (
          <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono">
            Last run: {new Date(lastChecked).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
          </span>
        )}
      </div>

      {persisted && !verified && persisted.last_failure_reason && (
        <div
          className="flex items-start gap-2 mt-3 p-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900"
          data-testid="razorpay-health-failure-reason"
        >
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-800 dark:text-amber-200 leading-relaxed">
            <span className="font-semibold">Last check failed:</span>{' '}
            {persisted.last_failure_reason}
          </p>
        </div>
      )}

      {persisted?.webhook_url && (
        <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-500 dark:text-slate-400">
          <Globe className="h-3 w-3" />
          <span className="font-mono break-all">{persisted.webhook_url}</span>
        </div>
      )}
    </div>
  );
}
